import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';

const ProfitPieChart = ({ calculations }) => {
  // Modern color palette with gradients
  const chartData = [
    {
      name: 'Total Cost',
      value: calculations?.totalCost || 0,
      color: '#8b5cf6', // Modern purple
      description: 'Direct + Overhead + Contingency'
    },
    {
      name: 'Gross Profit',
      value: calculations?.grossProfit || 0,
      color: '#06b6d4', // Modern cyan
      description: 'Net profit from sale'
    }
  ];

  const formatCurrency = (value) => {
    return `OMR ${parseFloat(value || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-white dark:bg-gray-800 border rounded-lg p-2.5 shadow-xl backdrop-blur-sm">
          <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">{data?.name}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">{data?.description}</p>
          <p className="text-base font-extrabold mb-1.5" style={{ color: data?.color }}>
            {formatCurrency(data?.value)}
          </p>
          <div className="flex items-center gap-1.5 pt-1.5 border-t border-gray-200 dark:border-gray-700">
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: data?.color }} />
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              {((data?.value / calculations?.sellPrice) * 100)?.toFixed(1)}% of Total
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }) => {
    return (
      <div className="flex flex-col gap-3 mt-4">
        {payload?.map((entry, index) => (
          <div key={index} className="flex items-center justify-between gap-4 bg-gradient-to-r from-gray-50 to-transparent dark:from-gray-800 dark:to-transparent rounded-lg p-3 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div
                className="w-4 h-4 rounded-full shadow-lg"
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-base font-semibold text-gray-800 dark:text-gray-200">{entry?.value}</span>
            </div>
            <span className="text-base font-bold text-gray-900 dark:text-white">
              {formatCurrency(entry?.payload?.value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const profitMarginPercentage = calculations?.profitMarginPercentage || 0;

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        className="font-bold text-sm"
        style={{ fontSize: '15px', fontWeight: '700', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
      >
        {`${(percent * 100)?.toFixed(1)}%`}
      </text>
    );
  };

  return (
    <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-cyan-50 dark:from-gray-900 dark:via-purple-900/20 dark:to-cyan-900/20 border-2 border-purple-200 dark:border-purple-800 rounded-2xl p-4 md:p-6 shadow-lg">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-gradient-to-br from-purple-500 to-cyan-500 rounded-lg shadow-md">
          <Icon name="PieChart" size={24} color="white" />
        </div>
        <h4 className="text-xl font-bold text-gray-900 dark:text-white">Profit Distribution</h4>
      </div>

      <div className="w-full" style={{ minHeight: '300px', maxHeight: '420px' }}>
        <ResponsiveContainer width="100%" height="100%" minHeight={300}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius="80%"
              innerRadius={0}
              fill="#8884d8"
              dataKey="value"
              animationBegin={0}
              animationDuration={1000}
              animationEasing="ease-out"
              strokeWidth={3}
              stroke="#fff"
            >
              {chartData?.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry?.color}
                  style={{
                    filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))'
                  }}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} verticalAlign="bottom" height={100} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t-2 border-purple-200 dark:border-purple-800">
        <div className="bg-gradient-to-br from-purple-100 to-purple-50 dark:from-purple-900/40 dark:to-purple-800/20 rounded-xl p-4 border border-purple-300 dark:border-purple-700 shadow-sm">
          <div className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-2">Selling Price</div>
          <div className="text-xl font-extrabold text-purple-900 dark:text-purple-100">
            {formatCurrency(calculations?.sellPrice)}
          </div>
        </div>
        <div className="bg-gradient-to-br from-cyan-100 to-cyan-50 dark:from-cyan-900/40 dark:to-cyan-800/20 rounded-xl p-4 border border-cyan-300 dark:border-cyan-700 shadow-sm">
          <div className="text-sm font-medium text-cyan-700 dark:text-cyan-300 mb-2">Gross Profit</div>
          <div className="text-xl font-extrabold text-cyan-900 dark:text-cyan-100">
            {formatCurrency(calculations?.grossProfit)}
          </div>
        </div>
        <div className="bg-gradient-to-br from-indigo-100 to-indigo-50 dark:from-indigo-900/40 dark:to-indigo-800/20 rounded-xl p-4 border border-indigo-300 dark:border-indigo-700 shadow-sm">
          <div className="text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-2">Profit Margin</div>
          <div className="text-xl font-extrabold text-indigo-900 dark:text-indigo-100">
            {profitMarginPercentage?.toFixed(2)}%
          </div>
        </div>
      </div>

      <div className="mt-4 bg-gradient-to-r from-purple-100 via-indigo-100 to-cyan-100 dark:from-purple-900/30 dark:via-indigo-900/30 dark:to-cyan-900/30 border-2 border-purple-300 dark:border-purple-700 rounded-xl p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="p-1.5 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
            <Icon name="Info" size={18} color="#8b5cf6" />
          </div>
          <div className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
            <span className="font-bold text-gray-900 dark:text-white">Visual Breakdown:</span> This chart illustrates how your selling price is allocated between total project costs and gross profit, providing clear insights into your project's financial structure.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitPieChart;